import dayjs from "dayjs";
import {
  CAPABILITY_REASONS,
  EVENT_PREFERENCE_KEYS,
  RELEASED_EVENT_PREFERENCE_KEYS,
  REMINDER_MINUTES,
  formatMatchupScore,
  formatMatchupTitle,
  formatPersonalizedTitle,
} from "@stanza/shared/event-personalization";

export const PERSONALIZATION_SCOPES = Object.freeze({
  CALENDAR: "calendar",
  DEFAULTS: "defaults",
});

/** Where a calendar sheet's save applies; the values are also the `apply_to` analytics dimension. */
export const APPLY_TO = Object.freeze({
  CALENDAR: "this_calendar",
  ALL: "all_calendars",
});

export const SAMPLE_CALENDAR_KINDS = Object.freeze(["sports", "tv", "manual"]);

export const REMINDER_LABELS = Object.freeze({
  none: "None",
  "30m": "30 minutes before",
  "2h": "2 hours before",
  "1d": "1 day before",
});

export const SEPARATOR_LABELS = Object.freeze({ original: "Original", vs: "vs", at: "@", dash: "–" });

export const WHERE_TO_WATCH_LABELS = Object.freeze({
  broadcast: "Where to watch",
  network: "Network / platform",
});

export const SPOILER_SAFE_HINTS = Object.freeze({
  scores: "Hide scores in past events",
  synopses: "Hide episode synopses",
});

export const COMING_SOON_COPY = "Coming soon";

/** Which calendars a default reaches, shown on each row of "My defaults". */
export const DEFAULT_APPLIES_TO = Object.freeze({
  titleStyle: "Applies to sports calendars",
  myTeamFirst: "Applies to team calendars",
  separator: "Sports calendars",
  emoji: "Applies to calendars you added",
  whereToWatch: "Applies to calendars with broadcast or network info",
  spoilerSafe: "Applies to sports and TV calendars",
  reminder: "Applies to calendars you added",
});

/** Capabilities per sample calendar kind, matching what the server derives for each. */
export const SAMPLE_CAPABILITIES = Object.freeze({
  sports: {
    titleStyle: { supported: true },
    myTeamFirst: { supported: true },
    separator: { supported: true },
    emoji: { supported: true },
    whereToWatch: { supported: true, source: "broadcast" },
    spoilerSafe: { supported: true, target: "scores" },
    reminder: { supported: true },
  },
  tv: {
    titleStyle: { supported: false, reason: CAPABILITY_REASONS.noMatchups },
    myTeamFirst: { supported: false, reason: CAPABILITY_REASONS.noMatchups },
    separator: { supported: false, reason: CAPABILITY_REASONS.noMatchups },
    emoji: { supported: true },
    whereToWatch: { supported: true, source: "network" },
    spoilerSafe: { supported: true, target: "synopses" },
    reminder: { supported: true },
  },
  manual: {
    titleStyle: { supported: false, reason: CAPABILITY_REASONS.noMatchups },
    myTeamFirst: { supported: false, reason: CAPABILITY_REASONS.noMatchups },
    separator: { supported: false, reason: CAPABILITY_REASONS.noMatchups },
    emoji: { supported: true },
    whereToWatch: { supported: false, reason: CAPABILITY_REASONS.noBroadcastData },
    spoilerSafe: { supported: false, reason: CAPABILITY_REASONS.noResultsOrSynopses },
    reminder: { supported: true },
  },
});

export const SAMPLE_CALENDAR_NAMES = Object.freeze({
  sports: "Golden State Warriors",
  tv: "Severance",
  manual: "SF Food Festivals",
});

/** Per preference key, which preview events its effect is visible on under the current preferences. */
const FOCUS_EVENT_MATCHERS = Object.freeze({
  myTeamFirst: (e, prefs) =>
    formatPersonalizedTitle(titleParts(e), { ...prefs, myTeamFirst: true }) !==
    formatPersonalizedTitle(titleParts(e), { ...prefs, myTeamFirst: false }),
  whereToWatch: (e) => Boolean(e.broadcast),
  spoilerSafe: (e) => Boolean(e.matchup?.winner || e.synopsis),
});

const DRAFT_STORAGE_PREFIX = "stanza.personalizationDraft.";
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const RECENT_DRAFT_MS = 30 * 60 * 1000;

/**
 * Sample preview events per calendar kind, dated relative to `now`, without details of unreleased rows.
 * @param {"sports" | "tv" | "manual"} kind
 * @param {Date} [now]
 * @returns {object[]} Preview events in the server's `previewEvents` shape.
 */
export function buildSamplePreviewEvents(kind, now = new Date()) {
  const at = (days, hour, minute = 0) =>
    dayjs(now).add(days, "day").hour(hour).minute(minute).second(0).millisecond(0);
  if (kind === "sports") {
    const next = {
      order: "away-first",
      separator: "at",
      home: { name: "Los Angeles Lakers", shortName: "LAL" },
      away: { name: "Golden State Warriors", shortName: "GSW" },
      prefix: null,
    };
    const final = {
      order: "away-first",
      separator: "at",
      home: { name: "Golden State Warriors", shortName: "GSW", score: 121 },
      away: { name: "Phoenix Suns", shortName: "PHX", score: 112 },
      prefix: null,
      winner: "home",
    };
    return releasedSampleDetails([
      {
        id: "sample-sports-next",
        kind: "sports",
        start: at(1, 19, 30).toISOString(),
        end: at(1, 22).toISOString(),
        isPast: false,
        title: formatMatchupTitle(next),
        matchup: next,
        mySide: "away",
        broadcast: "ESPN",
      },
      {
        id: "sample-sports-final",
        kind: "sports",
        start: at(-1, 19).toISOString(),
        end: at(-1, 21, 30).toISOString(),
        isPast: true,
        title: formatMatchupTitle(final),
        matchup: final,
        mySide: "home",
        broadcast: "NBC Sports Bay Area",
      },
    ]);
  }
  if (kind === "tv") {
    return releasedSampleDetails([
      {
        id: "sample-tv-next",
        kind: "tv",
        start: at(1, 21).toISOString(),
        end: at(1, 22).toISOString(),
        isPast: false,
        title: "Severance S3E4 · Cold Harbor",
        broadcast: "Apple TV+",
      },
      {
        id: "sample-tv-last",
        kind: "tv",
        start: at(-6, 21).toISOString(),
        end: at(-6, 22).toISOString(),
        isPast: true,
        title: "Severance S3E3",
        broadcast: "Apple TV+",
        synopsis: "Mark confronts the board as the severed floor turns on itself.",
      },
    ]);
  }
  return releasedSampleDetails([
    {
      id: "sample-manual-next",
      kind: "manual",
      start: at(1, 11).toISOString(),
      end: at(1, 18).toISOString(),
      isPast: false,
      title: "Mission Street Food Fair",
      location: "Valencia & 21st",
    },
    {
      id: "sample-manual-later",
      kind: "manual",
      start: at(3, 10).toISOString(),
      end: at(3, 14).toISOString(),
      isPast: false,
      title: "Ferry Building Farmers Market",
      location: "Embarcadero",
    },
  ]);
}

/**
 * Whether a sheet row is shown: supported keys, plus unreleased ones (shown as "Coming soon").
 * @param {{ supported?: boolean, reason?: string } | undefined} capability
 * @returns {boolean}
 */
export function isPreferenceRowShown(capability) {
  return capability?.supported === true || capability?.reason === CAPABILITY_REASONS.notYetAvailable;
}

/**
 * @param {object | null} capabilities
 * @returns {boolean} True when the server reports the whole calendar as never personalized.
 */
export function isNotPersonalizable(capabilities) {
  return Boolean(capabilities) && EVENT_PREFERENCE_KEYS.every(
    (key) => capabilities[key]?.reason === CAPABILITY_REASONS.notPersonalizable
  );
}

/**
 * Render one preview event under the effective preferences.
 * @param {object} event - A `previewEvents` entry.
 * @param {import("@stanza/shared/event-personalization").EventPreferences} prefs
 * @returns {{ title: string, when: string, watch: string | null, result: { text: string, hidden: boolean } | null, synopsis: { text: string, hidden: boolean } | null, location: string | null, alert: string | null }}
 */
export function renderPreviewEvent(event, prefs) {
  const parts = titleParts(event);
  const title = formatPersonalizedTitle(parts, prefs);
  // Rendered without spoiler-safe so the preview can mark where a score is hidden.
  const score = formatMatchupScore(parts, { ...prefs, spoilerSafe: false });
  const hasReminder = !event.isPast && REMINDER_MINUTES[prefs.reminder] != null;
  const reminderLabel = event.allDay ? "at 7 PM the day before" : REMINDER_LABELS[prefs.reminder]?.toLowerCase();
  return {
    title,
    when: formatPreviewWhen(event),
    watch: prefs.whereToWatch && event.broadcast ? event.broadcast : null,
    result: score
      ? prefs.spoilerSafe
        ? { text: "Score hidden", hidden: true }
        : { text: score, hidden: false }
      : null,
    synopsis: event.synopsis
      ? prefs.spoilerSafe
        ? { text: "Synopsis hidden", hidden: true }
        : { text: event.synopsis, hidden: false }
      : null,
    location: event.location ?? null,
    alert: hasReminder ? `Alert ${reminderLabel}` : null,
  };
}

/**
 * Day + time label for a preview event ("Fri, Oct 17 · 7:30 PM", "Sat, Oct 18 · All day", "Wed, Oct 15 · Final").
 * @param {{ start: string, allDay?: boolean, isPast?: boolean, matchup?: object }} event
 * @returns {string}
 */
export function formatPreviewWhen(event) {
  const day = dayjs(event.start).format("ddd, MMM D");
  if (event.isPast && event.matchup?.winner) return `${day} · Final`;
  if (event.allDay) return `${day} · All day`;
  return `${day} · ${dayjs(event.start).format("h:mm A")}`;
}

/**
 * The preview event that best shows a preference's effect: one the touched key changes, else the next upcoming.
 * @param {object[]} events
 * @param {string | null} lastTouchedKey
 * @param {import("@stanza/shared/event-personalization").EventPreferences} prefs - The preferences the preview renders with.
 * @returns {object | null}
 */
export function pickFocusEvent(events, lastTouchedKey, prefs) {
  if (!events?.length) return null;
  const matcher = FOCUS_EVENT_MATCHERS[lastTouchedKey];
  if (matcher) {
    const showsKey = (e) => matcher(e, prefs);
    const upcoming = events.filter((e) => !e.isPast && showsKey(e));
    const revealing = upcoming[0] ?? events.find(showsKey);
    if (revealing) return revealing;
  }
  return events.find((e) => !e.isPast) ?? events[0];
}

/**
 * Keys whose value differs between two override sets (missing and null count as equal).
 * @param {object} before
 * @param {object} after
 * @returns {string[]}
 */
export function changedPreferenceKeys(before = {}, after = {}) {
  return EVENT_PREFERENCE_KEYS.filter((key) => (before?.[key] ?? null) !== (after?.[key] ?? null));
}

/**
 * Only the keys that differ between two preference sets, with their new values (`null` for cleared).
 * @param {object} before
 * @param {object} after
 * @returns {object}
 */
export function changedPreferenceValues(before = {}, after = {}) {
  return Object.fromEntries(changedPreferenceKeys(before, after).map((key) => [key, after?.[key] ?? null]));
}

/**
 * The values a save picked, leaving out keys reset to inherit; only these can also become defaults.
 * @param {object} savedOverrides
 * @param {object} overrides
 * @returns {object}
 */
export function pickedPreferenceValues(savedOverrides = {}, overrides = {}) {
  return Object.fromEntries(
    Object.entries(changedPreferenceValues(savedOverrides, overrides)).filter(([, value]) => value != null)
  );
}

/**
 * The two writes behind "All my calendars": the picks become defaults, and this calendar inherits them.
 * Calendars with their own value for a key keep it.
 * @param {{ savedOverrides?: object, overrides: object, savedDefaults?: object }} args
 * @returns {{ calendarDefaults: object, preferences: object }} The PATCH `calendarDefaults` and the calendar PUT body.
 */
export function applyToAllPayloads({ savedOverrides, overrides, savedDefaults }) {
  const picks = pickedPreferenceValues(savedOverrides, overrides);
  const inherit = Object.fromEntries(Object.keys(picks).map((key) => [key, null]));
  return {
    calendarDefaults: toPreferencePayload({ ...savedDefaults, ...picks }),
    preferences: toPreferencePayload({ ...overrides, ...inherit }),
  };
}

/**
 * Up to three initials for a calendar avatar.
 * @param {string} [name]
 * @returns {string}
 */
export function calendarInitials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((word) => Array.from(word)[0].toUpperCase())
    .join("");
}

/**
 * Path + query to come back to after an upgrade, with `?customize=<id>` so the sheet reopens.
 * @param {string} calendarId
 * @param {Location} [location]
 * @returns {string}
 */
export function customizeReturnPath(calendarId, location = window.location) {
  const params = new URLSearchParams(location.search);
  params.set("customize", String(calendarId));
  return `${location.pathname}?${params.toString()}`;
}

/**
 * The same path with `?customize` dropped, so leaving the upgrade wall doesn't reopen the sheet.
 * @param {string} path - Path with an optional query string.
 * @returns {string}
 */
export function withoutCustomizeParam(path) {
  const [pathname, query = ""] = path.split("?");
  const params = new URLSearchParams(query);
  params.delete("customize");
  const rest = params.toString();
  return rest ? `${pathname}?${rest}` : pathname;
}

/**
 * Keep unsaved personalization edits across the upgrade round-trip.
 * @param {string} key - Calendar id, or `"defaults"` for the defaults page.
 * @param {{ overrides?: object, defaults?: object }} draft - Only the changed keys, applied over the saved state on restore.
 * @returns {void}
 */
export function stashPersonalizationDraft(key, draft) {
  try {
    window.sessionStorage.setItem(DRAFT_STORAGE_PREFIX + key, JSON.stringify({ ...draft, stashedAt: Date.now() }));
  } catch {
    // Storage blocked: the user re-picks after upgrading.
  }
}

/**
 * Read and clear a draft kept by `stashPersonalizationDraft`, dropping it when older than `maxAgeMs`.
 * @param {string} key
 * @param {{ maxAgeMs?: number }} [options]
 * @returns {object | null}
 */
export function takePersonalizationDraft(key, { maxAgeMs = DRAFT_MAX_AGE_MS } = {}) {
  try {
    const storageKey = DRAFT_STORAGE_PREFIX + key;
    const raw = window.sessionStorage.getItem(storageKey);
    window.sessionStorage.removeItem(storageKey);
    const draft = raw ? JSON.parse(raw) : null;
    return draft && Date.now() - (draft.stashedAt ?? 0) <= maxAgeMs ? draft : null;
  } catch {
    return null;
  }
}

/**
 * Take the stashed draft to restore on open: up to a day old on the upgrade return, else only a recent one.
 * @param {string} key
 * @param {{ isUpgradeReturn: boolean }} options
 * @returns {object | null}
 */
export function takeRestorableDraft(key, { isUpgradeReturn }) {
  return takePersonalizationDraft(key, isUpgradeReturn ? {} : { maxAgeMs: RECENT_DRAFT_MS });
}

/**
 * True while any restored draft value is still in place and still differs from the saved state.
 * @param {object | undefined} restored - The draft's changed keys and values.
 * @param {object | null} current
 * @param {object | undefined} saved
 * @returns {boolean}
 */
export function hasPendingRestoredChanges(restored, current, saved) {
  return Object.entries(restored ?? {}).some(
    ([key, value]) => (current?.[key] ?? null) === (value ?? null) && (saved?.[key] ?? null) !== (value ?? null)
  );
}

/**
 * The PUT body for a preference set: every key present, `null` for anything unset.
 * @param {object | null} values
 * @returns {object}
 */
export function toPreferencePayload(values) {
  return Object.fromEntries(EVENT_PREFERENCE_KEYS.map((key) => [key, values?.[key] ?? null]));
}

/**
 * The line shown when a personalization save fails.
 * @param {unknown} error - The rejected request.
 * @param {string} fallback - Copy for any failure other than a missing membership.
 * @returns {string}
 */
export function personalizationSaveError(error, fallback) {
  return error?.response?.data?.code === "STANZA_PLUS_REQUIRED"
    ? "Saving needs an active Stanza+ membership."
    : fallback;
}

/**
 * Whether a query holds data for its own key, fetched at or after `since`, so an editor can seed a
 * draft from it without picking up a stale cached copy.
 * @param {{ data?: unknown, isPreviousData?: boolean, dataUpdatedAt: number }} query
 * @param {number | null} since
 * @returns {boolean}
 */
export function isFetchedSince(query, since) {
  return since !== null && Boolean(query.data) && !query.isPreviousData && query.dataUpdatedAt >= since;
}

/**
 * Drop sample details that only an unreleased preference would show, so a sample renders like a feed today.
 * @param {object[]} events
 * @returns {object[]}
 */
function releasedSampleDetails(events) {
  if (RELEASED_EVENT_PREFERENCE_KEYS.includes("whereToWatch")) return events;
  return events.map(({ broadcast, ...event }) => event);
}

/**
 * @param {object} event - A `previewEvents` entry.
 * @returns {import("@stanza/shared/event-personalization").PersonalizedTitleParts}
 */
function titleParts(event) {
  return { title: event.title, matchup: event.matchup, mySide: event.mySide };
}
